import { Component } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UserService } from '@frontend/sports/user/feature';
import { ProductHomepagesConfig } from '@frontend/vanilla/core';
import { Store, createSelector } from '@ngrx/store';
import { Observable } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipError, ErrorOrigin } from '../../validation/errors/betslip-error';
import { selectCurrentVisibleBetslipErrorsByPriority, selectHasCurrentComboPreventionError } from '../../validation/selectors';
import { BetslipErrorMessageFormatterService } from '../../validation/services/formatting/betslip-error-message-formatter.service';
import { IExtendedMessage, isExtendedMessage } from '../../validation/services/formatting/message-formatters/message-formatter';
import { EditBetErrorService } from '../services/edit-bet-error.service';

const selectViewModel = createSelector(
    selectCurrentVisibleBetslipErrorsByPriority,
    selectHasCurrentComboPreventionError,
    (errors, hasComboPrevention) => ({ errors, hasComboPrevention }),
);

@Component({
    selector: 'bs-betslip-bet-errors',
    templateUrl: './edit-bet-errors.component.html',
    styleUrls: ['./edit-bet-errors.component.scss'],
    standalone: false,
})
export class EditBetErrorsComponent {
    viewModel$: Observable<{ errors: BetslipError[]; hasComboPrevention: boolean }>;

    constructor(
        public m2User: UserService,
        private productHomepagesConfig: ProductHomepagesConfig,
        private redirectHelper: RedirectHelperService,
        private errorMessageFormatter: BetslipErrorMessageFormatterService,
        public siteCore: Sitecore,
        private errorService: EditBetErrorService,
        store: Store<IBetslipRootState>,
    ) {
        this.viewModel$ = store.select(selectViewModel);
    }

    hasBetslipErrors(errors: BetslipError[]): boolean {
        return this.getBetslipErrors(errors).length > 0;
    }

    /**
     * Get all errors, that should be shown.
     *
     * @param errorsState
     */
    private getBetslipErrors(errors: BetslipError[]): BetslipError[] {
        return this.errorService.getError(errors);
    }

    /**
     * Take first error. We show always one error.
     *
     * @param errorsState
     */
    getError(errors: BetslipError[]): BetslipError | null {
        const betslipErrors = this.getBetslipErrors(errors);

        return betslipErrors[0] || null;
    }

    /**
     * Get the message for the error
     *
     * @param error
     */
    getErrorMessage(error: BetslipError): Observable<Nullable<string | IExtendedMessage>> {
        return this.errorMessageFormatter.getErrorMessage(error);
    }

    isExtendedMessage(message: string | IExtendedMessage): message is IExtendedMessage & boolean {
        return isExtendedMessage(message);
    }

    /**
     * What to do when user clicks on the error.
     *
     * @param $event
     * @param error
     */
    redirectFromErrorMessage($event: any, error: BetslipError): void {
        if (!$event?.target?.className) {
            return;
        }

        if ($event.target.className.indexOf('viewbonus') > -1) {
            this.redirectHelper.goToPage(this.productHomepagesConfig.portal + '/mobileportal/mybonuses');
        } else if ($event.target.className.indexOf('docuploadstatuscheck') > -1) {
            this.redirectHelper.goToPage(this.productHomepagesConfig.portal + '/mobileportal/docuploadstatuscheck');
        }
    }

    /**
     * Check if error comes from BPS.
     *
     * @param error
     */
    isBetNotPlaced(error: BetslipError): boolean {
        return error.origin === ErrorOrigin.BetPlacement;
    }
}
